import { useEffect, type ReactNode } from 'react';

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Sticky header — title, meta, whatever belongs above the scroll. */
  header?: ReactNode;
  /** Sticky footer — pinned to the bottom of the panel, below the scroll. */
  footer?: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
  testId?: string;
}

/**
 * A record opened from a list, shown over the right half of the screen.
 *
 * The list stays where it is: an inline expansion pushes every row below it
 * down, so opening the third item moves the fourth out from under the cursor
 * and the page you were scanning rearranges itself while you read.
 *
 * Geometry and easing match the Error Dashboard's drawer — that screen already
 * established this as how this app opens one record — and the shell is shared
 * so the two cannot drift apart. Below 900px it takes the full width, because
 * half a phone is not a readable column.
 *
 * Escape closes it, and the body is locked while it is open so a scroll inside
 * the drawer does not scroll the list underneath.
 */
const SideDrawer = ({ open, onClose, header, footer, children, ariaLabel, testId }: SideDrawerProps) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // A dialog opened from inside the drawer owns Escape first — closing both
      // at once takes away the record you were reading along with the file you
      // opened from it.
      if (document.querySelector('.p-dialog-mask')) return;
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <>
      <div className={`sui-scrim ${open ? 'show' : ''}`} onClick={onClose} aria-hidden="true" />
      <aside
        className={`sui-drawer ${open ? 'show' : ''}`}
        aria-label={ariaLabel}
        aria-hidden={!open}
        data-testid={testId}
      >
        {/* Mounted only while open: a closed drawer holding a previous record
            would flash it on the way in the next time one is opened. */}
        {open && (
          <>
            {header}
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
            {footer}
          </>
        )}
      </aside>
    </>
  );
};

export default SideDrawer;
